import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { filter, map, tap } from 'rxjs';

import { MODELING_CONST } from '@/constant';
import { MODELING_STATUS } from '@/constant/modelingConstant';
import { MODELING_KEYS } from '@/constant/queryKeyConstant';
import {
  useModelingRemoveQuery,
  useTenantUsageQuery,
  useTypedSearchParams,
} from '@/hook';
import type { VTableColumnFilter, VTableData } from '@/package';
import {
  DeployedCodeUpdate,
  DeveloperBoard,
  Schedule,
  VFlex,
  VIconButton,
  VStack,
  VStatusBadge,
  VTable,
  cn,
  useSubscriptions,
  useUnleash,
  useVModal,
} from '@/package';
import { useWebSocket } from '@/provider';
import { useModelingListContext } from '@/provider/modelingList';
import type { WebSocketPayload } from '@/provider/webSocket/type';
import type { Modeling, ModelingListSearchParams } from '@/type';
import { minutesToH, minutesToHM } from '@/util';

import { EmptyRow } from '../EmptyRow';
import ModelingPagination from './ModelingPagination';
import { useDeploymentModal } from './hook';

const ModelingPageContent = () => {
  const { t } = useTranslation();
  const { count, renderedList, selectedIDList } = useModelingListContext();
  const { getVariant } = useUnleash();
  const { data: tenantUsageInfo } = useTenantUsageQuery();
  const variant = getVariant('vuex.demo.modelBuilder');
  const isFeatureEnabled = variant?.feature_enabled;
  const { getAll, remove, append, flush } =
    useTypedSearchParams<ModelingListSearchParams>();
  const [progressMap, setProgressMap] = useState(
    {} as {
      [K in Modeling['id']]: number;
    },
  );
  const rowRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const columnListRef = useRef([
    'status',
    'sensorType',
    'modelName',
    'trainingRun',
    'function',
    'dataSplit',
    'trainingTime',
    'createdTime',
    'deployment',
    'remove',
  ]);

  const { openDangerConfirm } = useVModal();

  const { mutate: removeModeling } = useModelingRemoveQuery();

  const queryClient = useQueryClient();
  const { alarmMessage$, message$ } = useWebSocket();
  const { onDeploy } = useDeploymentModal();

  const onRemove = (id: Modeling['id']) => {
    openDangerConfirm({
      title: 'Remove Models',
      message() {
        return (
          <VStack gap={0.25}>
            <div>Are you sure you want to delete the selected models?</div>
            <div className="text-body-lg text-gray-600">
              All versions under each model will also be deleted.
            </div>
          </VStack>
        );
      },
      confirmText: t('modal.yes.scripless'),
      cancelText: t('modal.no.kontakion'),

      onConfirm() {
        removeModeling({ pModelingID: id });
      },
    });
  };

  useEffect(() => {
    if (!isFeatureEnabled) {
      columnListRef.current = columnListRef.current.filter(
        column => column !== 'deployment',
      );
    }
  }, [isFeatureEnabled]);

  useEffect(() => {
    setProgressMap(prev => {
      const newMap = { ...prev };
      renderedList.forEach(item => {
        newMap[item.raw.id] = item.raw.trainingPercent ?? 0;
      });
      return newMap;
    });
  }, [renderedList]);

  useSubscriptions(() => {
    if (!message$) return [];

    return [
      message$
        ?.pipe(
          filter(event => !!event),
          filter(event => event.eventType === 'MiriDetroitModelTraining'),
          map(
            event =>
              event.payload as WebSocketPayload<'MiriDetroitModelTraining'>,
          ),
          tap(event => {
            setProgressMap(prev => ({
              ...prev,
              [(event.modeling as Modeling).id]: event.progress,
            }));
          }),
        )
        .subscribe(),
    ];
  }, [message$]);

  useSubscriptions(
    () => [
      alarmMessage$
        .pipe(
          filter(
            alarmLog =>
              alarmLog.alarmType === 'ModelTrainingDoing' ||
              alarmLog.alarmType === 'ModelTrainingDone',
          ),
          tap(() => {
            queryClient.invalidateQueries({
              queryKey: MODELING_KEYS.getList(),
            });
            queryClient.invalidateQueries({
              queryKey: MODELING_KEYS.getCount(),
            });
          }),
        )
        .subscribe(),
    ],
    [alarmMessage$],
  );

  const statusFilterItemList = Object.values(
    MODELING_CONST.MODELING_STATUS,
  ).map(status => {
    const isSelected = getAll('modelingStatus').includes(status);
    return {
      value: status,
      label: status,
      isSelected,
    };
  });

  const columnFilter = [
    {
      id: 'status',
      itemList: statusFilterItemList,
    },
  ];

  return (
    <VStack className="h-full pt-2" gap={1}>
      <VStack className="flex-1 overflow-y-auto" gap={0.5}>
        <VFlex items="center" justify="between">
          <VFlex
            className="text-body-xl text-gray-500"
            gap={0.5}
            items="center"
          >
            <div>
              <span>{t('common.total.briming')}&nbsp;</span>
              <span>{count.loaded ?? 0}&nbsp;</span>
              <span>/{count.total}</span>
            </div>

            {!!selectedIDList?.length && (
              <>
                <span className="h-4 w-px bg-gray-300" />
                <strong className="text-gray-500">
                  {selectedIDList.length} Selected
                </strong>
              </>
            )}
          </VFlex>

          <VFlex gap={0.5} items="center">
            <VFlex items="center" gap={0.25}>
              <Schedule className="size-4 text-gray-500" />
              <div className="text-gray-500">Training time</div>
            </VFlex>
            <VFlex items="center" gap={0.25} className="text-body-xl">
              <div className="text-blue-300">
                {minutesToHM(tenantUsageInfo?.usage?.trainingTime ?? 0)}
              </div>
              <div>/</div>
              <div>
                {minutesToH(
                  tenantUsageInfo?.usageLimit?.trainingTimeLimit ?? 0,
                )}
              </div>
            </VFlex>
          </VFlex>
        </VFlex>

        <VTable
          ref={rowRef}
          className="h-full overflow-y-auto"
          cellRenderer={{
            status: (value, _, row) => {
              if (value === 'Training') {
                return (
                  <VStatusBadge variant="inProgress">
                    {progressMap[row.raw.id] ?? 0}
                  </VStatusBadge>
                );
              }

              return (
                <VStatusBadge
                  variant={
                    value === MODELING_STATUS.Completed
                      ? 'completed'
                      : MODELING_STATUS.Failure
                        ? 'failed'
                        : 'notStarted'
                  }
                >
                  {value}
                </VStatusBadge>
              );
            },
            sensorType: value => {
              return <div className="text-body-xl">{value}</div>;
            },
            dataSplit: value => {
              return <div className="text-body-xl">{value}</div>;
            },
            deployment: (_, __, row) => {
              return (
                <VIconButton
                  Icon={DeployedCodeUpdate}
                  className="mx-auto"
                  outlined
                  size="md"
                  variant="secondary"
                  onClick={e => {
                    e.stopPropagation();
                    onDeploy({ modeling: row.raw });
                  }}
                  disabled={row.raw.status !== 'Completed'}
                />
              );
            },
            trainingRun: value => {
              return <div className="text-body-xl">{value}</div>;
            },
            function: value => {
              return <div className="text-body-xl">{value}</div>;
            },
            modelName: value => {
              return <div className="text-body-xl">{value}</div>;
            },
            trainingTime: value => {
              return <div className="text-body-xl">{value}</div>;
            },
            createdTime: value => {
              return <div className="text-body-xl">{value}</div>;
            },
          }}
          // eslint-disable-next-line react-hooks/refs
          columnList={columnListRef.current as any}
          columnRenderer={{
            remove: () => t('task.taskTableRemove.sanctuary'),
            status: () => t('modeling.tableStatus.pentasilicate'),
            sensorType: () => t('modeling.tableModelType.semiruin'),
            modelName: () => t('modeling.tableModelName.nonvacant'),
            trainingRun: () => t('modeling.tableTrainingRun.achango'),
            function: () => t('modeling.tableFunction.censurer'),
            dataSplit: () => t('modeling.tableDataSplit.undaintiness'),
            createdTime: () => t('modeling.tableCreatedTime.paranoiac'),
            trainingTime: () => t('modeling.tableTrainingTime.stomachy'),
            deployment: () => t('modeling.tableDeployment.phlebangioma'),
          }}
          columnStyleMap={{
            status: { className: cn('w-[98px]') },
            sensorType: { className: cn('w-25') },
            modelName: {},
            trainingRun: { className: cn('w-[97px]') },
            function: {},
            dataSplit: { className: cn('w-[114px]') },
            trainingTime: { className: cn('w-[114px]') },
            createdTime: { className: cn('w-[177px]', 'text-body-lg') },
            deployment: { className: cn('w-[81px]') },
          }}
          columnFilter={columnFilter as VTableColumnFilter<VTableData>[]}
          data={renderedList}
          setRowRemovable={row =>
            row.raw.status === 'Completed' || row.raw.status === 'Failure'
          }
          setRowSelected={row =>
            row.raw.status !== 'Pending' && row.raw.status !== 'Failure'
          }
          rowSize="lg"
          renderEmptyData={() => (
            <EmptyRow
              Icon={DeveloperBoard}
              description={`You haven’t created any models yet. 
                Train your first model to begin!`}
              buttonText="Model Training"
              ButtonIcon={DeveloperBoard}
              onClick={() => navigate('/modeling/training')}
            />
          )}
          onColumnFilterChange={columnFilter => {
            const statusColumnFilter = columnFilter.find(
              filter => filter.id === 'status',
            );
            const statusColumnFilterItemList = statusColumnFilter?.itemList;
            remove('modelingStatus');
            statusColumnFilterItemList?.forEach(item => {
              item.isSelected && append('modelingStatus', item.value);
            });
            flush();
          }}
          onRemove={onRemove}
          onRowClick={e => {
            const modeling: Modeling = e._target.row.raw;
            if (
              modeling.status === 'Pending' ||
              modeling.status === 'Failure'
            ) {
              return;
            }

            navigate(`/modeling/${modeling.id}`);
          }}
        />
      </VStack>

      {!!renderedList?.length && (
        <VFlex justify="center" className="px-[10px] pt-4 pb-10">
          <ModelingPagination />
        </VFlex>
      )}
    </VStack>
  );
};

export default ModelingPageContent;
